import { isFeatureClip, type HelpEntry } from './help-types';

/**
 * Pure helpers for the Help gallery (Round 9, Q48 to Q50): tile labels, search, and the
 * "Up next" queue the shared player moves through.
 */

/** `84` → `"1:24"`. */
export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** The badge on a tile: Flow, Task (subsection video) or Clip (feature clip). */
export function kindLabel(entry: Pick<HelpEntry, 'kind'>): 'Flow' | 'Task' | 'Clip' {
  if (entry.kind === 'flow') return 'Flow';
  return isFeatureClip(entry) ? 'Clip' : 'Task';
}

/** Case-insensitive match of every word of `query` against the title and purpose. */
export function matchesSearch(entry: Pick<HelpEntry, 'title' | 'purpose'>, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = `${entry.title} ${entry.purpose}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** Task videos and Flows first, then feature clips, each in registry order. */
export function tasksThenClips(entries: readonly HelpEntry[]): HelpEntry[] {
  return [...entries.filter((entry) => !isFeatureClip(entry)), ...entries.filter(isFeatureClip)];
}

/** Entries with a published video first, then the "Coming soon" ones, each in their order. */
export function publishedFirst(entries: readonly HelpEntry[]): HelpEntry[] {
  return [...entries.filter((entry) => entry.video !== null), ...entries.filter((entry) => entry.video === null)];
}

export interface Suggestion {
  entry: HelpEntry;
  /** "start": the first Flow, for someone new to Harry; "next": the next video they have not watched. */
  reason: 'start' | 'next';
}

/**
 * The one video the Help page puts first: the first published Flow until it is watched,
 * then the first published video not watched yet. Null once everything is watched.
 */
export function suggestNext(entries: readonly HelpEntry[], watched: ReadonlySet<string>): Suggestion | null {
  const published = entries.filter((entry) => entry.video !== null && !watched.has(entry.id));
  const flow = published.find((entry) => entry.kind === 'flow');
  if (flow && !entries.some((entry) => entry.kind === 'flow' && watched.has(entry.id))) return { entry: flow, reason: 'start' };
  const next = published.find((entry) => !isFeatureClip(entry)) ?? published[0];
  return next ? { entry: next, reason: 'next' } : null;
}

/**
 * What plays after `current`, in order:
 * - a Flow plays the Flows after it (`all` is in registry order, so "First client" leads
 *   into "From assessment to drill plan"); a viewer who cannot see a later Flow is not queued it;
 * - anything else plays the rest of `context` after it (the section or the screen's help).
 * Only entries with a published video are queued.
 */
export function upNextQueue(current: HelpEntry, context: readonly HelpEntry[], all: readonly HelpEntry[]): HelpEntry[] {
  const list = current.kind === 'flow' ? all.filter((entry) => entry.kind === 'flow') : context;
  const candidates = list.slice(list.findIndex((entry) => entry.id === current.id) + 1);
  return candidates.filter((entry) => entry.id !== current.id && entry.video !== null);
}
